import React, { useState, useRef, useEffect } from "react";
import "./styles.css";

const welcomeMessages = [
  "Hi! How can I help you today?",
  "Ask me about our services, tech stack, clients, team, or office locations!",
  "For more info, I can connect you to WhatsApp.",
];

const starterQuestions = [
  {
    question: "What are your requirements?",
    answer:
      "Please share your business requirements. Our team will guide you with the best solution!",
  },
  {
    question: "Which service are you interested in?",
    answer:
      "We offer SharePoint, Power Platform, Data Analytics, AI, and more. Let us know your area of interest.",
  },
  {
    question: "Would you like to set up a meeting?",
    answer:
      "You can schedule a meeting with us at technoserveitsolutions@gmail.com. Click below to email us!",
    meeting: true,
  },
];

function getGoogleCalendarLink() {
  const base =
    "https://calendar.google.com/calendar/render?action=TEMPLATE";
  const title = encodeURIComponent("Meeting with Technoserve");
  const details = encodeURIComponent(
    "Let's discuss your requirements with Technoserve."
  );
  const location = encodeURIComponent("Online");
  const start = new Date(Date.now() + 86400000);
  const yyyy = start.getFullYear();
  const mm = String(start.getMonth() + 1).padStart(2, "0");
  const dd = String(start.getDate()).padStart(2, "0");
  const hh = String(start.getHours()).padStart(2, "0");
  const min = String(start.getMinutes()).padStart(2, "0");
  const startStr = `${yyyy}${mm}${dd}T${hh}${min}00Z`;
  const end = new Date(start.getTime() + 30 * 60000);
  const ehh = String(end.getHours()).padStart(2, "0");
  const emin = String(end.getMinutes()).padStart(2, "0");
  const endStr = `${yyyy}${mm}${dd}T${ehh}${emin}00Z`;
  return `${base}&text=${title}&details=${details}&location=${location}&dates=${startStr}/${endStr}&add=technoserveitsolutions@gmail.com`;
}

function getOutlookCalendarLink() {
  const base = "https://outlook.live.com/calendar/0/deeplink/compose";
  const subject = encodeURIComponent("Meeting with Technoserve");
  const body = encodeURIComponent(
    "Let's discuss your requirements with Technoserve."
  );
  const start = new Date(Date.now() + 86400000);
  const yyyy = start.getFullYear();
  const mm = String(start.getMonth() + 1).padStart(2, "0");
  const dd = String(start.getDate()).padStart(2, "0");
  const hh = String(start.getHours()).padStart(2, "0");
  const min = String(start.getMinutes()).padStart(2, "0");
  const startStr = `${yyyy}-${mm}-${dd}T${hh}:${min}:00`;
  const end = new Date(start.getTime() + 30 * 60000);
  const ehh = String(end.getHours()).padStart(2, "0");
  const emin = String(end.getMinutes()).padStart(2, "0");
  const endStr = `${yyyy}-${mm}-${dd}T${ehh}:${emin}:00`;
  return `${base}?subject=${subject}&body=${body}&startdt=${startStr}&enddt=${endStr}&to=technoserveitsolutions@gmail.com`;
}

const ChatBot = () => {
  const [open, setOpen] = useState(false);
  // Load chat history from localStorage or start with welcome message
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("chatbotMessages");
    return saved
      ? JSON.parse(saved)
      : [{ from: "bot", text: welcomeMessages[0] }];
  });
  const [input, setInput] = useState("");
  const [suggested, setSuggested] = useState(starterQuestions);
  const chatEndRef = useRef(null);
  const [meetingLinks, setMeetingLinks] = useState(false);

  useEffect(() => {
    if (open && chatEndRef.current)
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const [loading, setLoading] = useState(false);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("chatbotMessages", JSON.stringify(messages));
  }, [messages]);

  const handleSuggestedClick = (q) => {
    setMessages((msgs) => [
      ...msgs,
      { from: "user", text: q.question },
      { from: "bot", text: q.answer, meeting: !!q.meeting },
    ]);
    if (q.meeting) {
      setTimeout(() => setMeetingLinks(true), 500);
    }
    setSuggested([]);
  };

  const send = async () => {
    const cleaned = input.trim();
    // Ignore empty or non-alphabetic inputs
    if (!cleaned || !/[a-zA-Z]/.test(cleaned)) {
      setMessages((msgs) => [
        ...msgs,
        { from: "bot", text: "Please enter a complete question or message." },
      ]);
      setInput("");
      return;
    }
    // Hide starter questions when user types any freeform question
    setSuggested([]);
    const userMsg = cleaned;
    const newMessages = [...messages, { from: "user", text: userMsg }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    // Meeting intent detection (simple keyword match)
    const meetingKeywords = ["meeting", "schedule", "calendar"];
    if (meetingKeywords.some((kw) => userMsg.toLowerCase().includes(kw))) {
      setTimeout(() => {
        setMessages((msgs) => [
          ...msgs,
          {
            from: "bot",
            text: "You can schedule a meeting with us at technoserveitsolutions@gmail.com. Choose a calendar below:",
            meetingLinks: true,
            special: true,
          },
        ]);
        setLoading(false);
      }, 400);
      return; // Do NOT fetch backend answer for meeting intent
    }

    try {
      const res = await fetch("/api/chatgpt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          history: newMessages.map((m) => ({ from: m.from, text: m.text })),
        }),
      });
      const data = await res.json();
      setMessages((msgs) => [
        ...msgs,
        { from: "bot", text: data.reply || "Sorry, no answer." },
      ]);
    } catch (err) {
      setMessages((msgs) => [
        ...msgs,
        { from: "bot", text: "Sorry, something went wrong. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };



  const handleClearChat = () => {
    const welcome = [{ from: "bot", text: welcomeMessages[0] }];
    setMessages(welcome);
    setSuggested(starterQuestions); // Show starter questions again
    setMeetingLinks(false); // Hide meeting links
    localStorage.setItem("chatbotMessages", JSON.stringify(welcome));
  };

  useEffect(() => {
    if (open && suggested.length === 0) {
      setSuggested(starterQuestions);
      setMeetingLinks(false);
    }
  }, [open]);

  return (
    <div className={`chatbot-widget${open ? " open" : ""}`}>
      {open ? (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <span>Chat with TechnoServe AI Agent Assistant</span>
            <button className="chatbot-close" onClick={() => setOpen(false)}>
              &times;
            </button>
          </div>
          <div className="chatbot-messages">
            {messages.map((msg, i) => {
  // Special meeting prompt
  if (msg.meetingLinks || msg.special) {
    return (
      <div key={i} className="chatbot-msg-row">
        <div className="chatbot-msg special chatbot-msg-bot">
          <span className="chatbot-avatar" title="Technoserve AI">TS</span>
          <span style={{flex:1}}>
            {msg.text}
            <div className="chatbot-meeting-links" style={{marginTop:'0.7rem', display:'flex', gap:'0.6rem'}}>
              <a href={getGoogleCalendarLink()} target="_blank" rel="noopener noreferrer" className="chatbot-calendar-btn">Google Calendar</a>
              <a href={getOutlookCalendarLink()} target="_blank" rel="noopener noreferrer" className="chatbot-calendar-btn">Outlook</a>
              <a href="mailto:technoserveitsolutions@gmail.com" className="chatbot-calendar-btn">Email Us</a>
            </div>
          </span>
        </div>
      </div>
    );
  }
  // User message
  if (msg.from === "user") {
    return (
      <div key={i} className="chatbot-msg-row" style={{justifyContent:'flex-end'}}>
        <div className="chatbot-msg chatbot-msg-user">
          <span className="chatbot-avatar" title="You">🧑</span>
          <span>{msg.text}</span>
        </div>
      </div>
    );
  }
  // Bot message
  return (
    <div key={i} className="chatbot-msg-row" style={{justifyContent:'flex-start'}}>
      <div className="chatbot-msg chatbot-msg-bot">
        <span className="chatbot-avatar" title="Technoserve AI">TS</span>
        <span>
          {msg.text}
          {msg.meeting && (
            <a
              href="mailto:technoserveitsolutions@gmail.com"
              style={{ marginLeft: 8, color: "#0077cc", textDecoration: "underline" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              Email Us
            </a>
          )}
        </span>
      </div>
    </div>
  );
})}
            {suggested.length > 0 && (
              <div className="chatbot-suggested">
                {suggested.map((q, i) => (
                  <button key={i} className="chatbot-suggested-btn" onClick={() => handleSuggestedClick(q)}>
                    {q.question}
                  </button>
                ))}
              </div>
            )}
            {meetingLinks && (
              <div style={{ display: "flex", gap: 8, marginTop: 8, marginBottom: 8 }}>
                <a href={getGoogleCalendarLink()} target="_blank" rel="noopener noreferrer" className="chatbot-calendar-btn">Google Calendar</a>
                <a href={getOutlookCalendarLink()} target="_blank" rel="noopener noreferrer" className="chatbot-calendar-btn">Outlook</a>
                <a href="mailto:technoserveitsolutions@gmail.com" className="chatbot-calendar-btn">Email Us</a>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="chatbot-input-row">
            <input
              className="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your question..."
              aria-label="Chat input"
              disabled={loading}
            />
            <button
              className="chatbot-send-btn"
              onClick={send}
              disabled={loading || !input.trim()}
              aria-label="Send message"
            >
              ➤
            </button>
            <button
              className="chatbot-clear-btn"
              onClick={handleClearChat}
              disabled={loading}
              aria-label="Clear chat"
              style={{
                verticalAlign: "middle",
                marginLeft: "0.5rem",
                background: "#fff",
                color: "#333",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              <span class="material-icons material-symbols-outlined">
                cancel
              </span>{" "}
            </button>
          </div>
          {loading && (
            <div
              style={{
                textAlign: "center",
                color: "#00bfff",
                padding: "0.5rem",
              }}
            >
              Thinking...
            </div>
          )}
         <a
  href="https://wa.me/7788994487?text=Hello%20Tech%20Partner!%20I%20visited%20your%20website."
  target="_blank"
  rel="noopener noreferrer"
  className="chatbot-whatsapp"
>
            WhatsApps
          </a>
        </div>
      ) : (
        <button
          className="chatbot-fab"
          onClick={() => setOpen(true)}
          aria-label="Open chat bot"
        >
          <span role="img" aria-label="Chat">
            💬
          </span>
        </button>
      )}
    </div>
  );
};

export default ChatBot;
